import { useState } from 'react';
import { Drawer, Table, Space, Button, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { api, type ArticleRow } from '../api/client';
import { useIsMobile } from '../hooks/useIsMobile';
import SelectedRelated from './SelectedRelated';
import { relatedColumns, relatedMobileColumns } from './related/relatedColumns';
import RelatedPreviewDrawer from './related/RelatedPreviewDrawer';
import RelatedFilters, { type RelatedFilterValues as Filters } from './related/RelatedFilters';

interface Props {
  open: boolean;
  excludeId: string;
  selectedIds: string[];
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
}

const PAGE_SIZE = 50;

function buildParams(f: Filters, page: number): Record<string, string> {
  const p: Record<string, string> = { page: String(page), pageSize: String(PAGE_SIZE) };
  if (f.title) p.title = f.title;
  if (f.status) p.status = f.status;
  if (f.categoryId) p.categoryId = f.categoryId;
  if (f.authorId) p.authorId = f.authorId;
  if (f.tagId) p.tagId = f.tagId;
  if (f.dateFrom) p.dateFrom = String(f.dateFrom);
  if (f.dateTo) p.dateTo = String(f.dateTo);
  return p;
}

/** 全版 Drawer：用與文章列表相同的多維度篩選（標題／分類／作者／狀態／日期）挑選關聯新聞。 */
export default function RelatedDrawer({ open, excludeId, selectedIds, onClose, onConfirm }: Props) {
  const [f, setF] = useState<Filters>({ title: '' });
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<string[]>(selectedIds);
  const [preview, setPreview] = useState<string | null>(null);
  const mobile = useIsMobile();

  const toggle = (id: string): void =>
    setPicked((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const patch = (p: Partial<Filters>) => { setF((s) => ({ ...s, ...p })); setPage(1); };

  const params = buildParams(f, page);
  const list = useQuery({
    queryKey: ['related-search', params],
    queryFn: () => api.listArticles(params),
    enabled: open,
  });
  const rows = (list.data?.items ?? []).filter((a) => a.id !== excludeId);

  return (
    <Drawer
      title="挑選關聯新聞"
      open={open}
      onClose={onClose}
      width="100%"
      // 手機：內距縮小，讓格線／表格多一點空間
      classNames={{ body: 'max-md:!p-3' }}
      extra={
        <Space size={mobile ? 4 : 8}>
          <Tag>{picked.length} 篇已選</Tag>
          {/* 手機標題列擠不下，取消交給右上角的 ✕ */}
          {!mobile && <Button onClick={onClose}>取消</Button>}
          <Button type="primary" onClick={() => onConfirm(picked)}>
            確定
          </Button>
        </Space>
      }
    >
      <RelatedFilters open={open} onChange={patch} />
      <Table<ArticleRow>
        rowKey="id"
        loading={list.isLoading}
        dataSource={rows}
        size={mobile ? 'small' : undefined}
        // 手機：整個 Drawer 一起捲，不另外鎖表格高度（calc 扣完在手機上幾乎沒剩）
        scroll={mobile ? undefined : { y: 'calc(100vh - 28rem)' }}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: picked,
          onChange: (keys) => setPicked(keys as string[]),
        }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: list.data?.total ?? 0,
          onChange: setPage,
          showSizeChanger: false,
          simple: mobile,
          showTotal: (t) => `共 ${t} 篇`,
        }}
        columns={mobile ? relatedMobileColumns : relatedColumns}
        onRow={(r) => ({
          className: 'cursor-pointer',
          onClick: (e) => {
            // 勾選框那一格交給 rowSelection，不要順便開預覽
            if ((e.target as HTMLElement).closest('.ant-table-selection-column')) return;
            setPreview(r.id);
          },
        })}
      />

      {/* 已選清單放表格下方：換頁/換篩選後仍看得到自己挑了哪幾篇 */}
      <SelectedRelated ids={picked} onRemove={(id) => setPicked((s) => s.filter((x) => x !== id))} />

      <RelatedPreviewDrawer
        articleId={preview}
        picked={!!preview && picked.includes(preview)}
        onTogglePick={toggle}
        onClose={() => setPreview(null)}
      />
    </Drawer>
  );
}
